import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import DataTable from '../../../components/ui/DataTable';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import PersonCell from '../../../components/ui/PersonCell';
import Tooltip from '../../../components/ui/Tooltip';
import { parseTableParams } from '../../../utils/Utils';
import { usePeriodParticipantsApi } from '../config/participants';
import PeriodNotice, { periodNoticeKey } from './PeriodNotice';

const ADDABLE = ['active', 'evaluation'];
const EDITABLE = ['active', 'evaluation', 'calibration'];
const STATUSES = ['active', 'excluded', 'left'];
export const EXCLUSION_REASONS = ['long_leave', 'parental_leave', 'role_change', 'other'];

const SnapValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.text.primary};
  svg { color: ${({ theme }) => theme.colors.text.muted}; flex-shrink: 0; }
`;

// Final inceleme I8 — geçişle doldurulmuş satırın departman/yönetici bilgisi dönem başının
// değil geçiş anının örgüt yapısıdır; değer işaretlenir ki tarihsel sanılmasın.
function SnapCell({ value, backfilled, hint }) {
  return (
    <SnapValue>
      {value}
      {backfilled && value && (
        <Tooltip text={hint}>
          <History size={14} aria-label={hint} />
        </Tooltip>
      )}
    </SnapValue>
  );
}

// Spec 2026-10-05 §9/1 — dönem katılımcıları (yalnız İK). Ekleme yalnız hedef/değerlendirme
// aşamasında; çıkarma ve geri alma kalibrasyonda da açık; doğrulanmamış dönemde işlem yok.
export default function PeriodParticipantsTab({ period }) {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState(null);
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ mode: null, row: null });
  const { fetching, data, pagination, add, exclude, reinclude } = usePeriodParticipantsApi(period?.id, queryParams);

  const verified = Boolean(period?.participantScopeVerifiedAt);
  const canAdd = verified && ADDABLE.includes(period?.status);
  const canEdit = verified && EDITABLE.includes(period?.status);
  const noticeKey = periodNoticeKey(period);

  const employeeAsync = useMemo(() => ({
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({ id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture ?? null }));
    },
    onGetById: async (id) => {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data || {};
      return { id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture ?? null };
    },
  }), []);

  const columns = useMemo(() => [
    {
      name: 'employeeName',
      label: t('performance.period.participants.employee'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => <PersonCell name={v} profilePicture={data[meta.rowIndex]?.profilePicture} />,
      },
    },
    ...['snapDepartmentName', 'snapManagerName'].map((name) => ({
      name,
      label: t(name === 'snapDepartmentName' ? 'performance.period.participants.department' : 'performance.period.participants.manager'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, meta) => (
          <SnapCell value={v} backfilled={Boolean(data[meta.rowIndex]?.snapshotIsBackfilled)} hint={t('performance.period.participants.snapshotBackfilled')} />
        ),
      },
    })),
    {
      name: 'status',
      label: t('performance.period.participants.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => t(`performance.period.participants.statuses.${v}`, { defaultValue: v }),
        filterOptions: {
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.period.participants.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.period.participants.statuses.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'source',
      label: t('performance.period.participants.source'),
      options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.period.participants.sources.${v}`, { defaultValue: v }) },
    },
    {
      name: 'exclusionReasonCode',
      label: t('performance.period.participants.reason'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t(`performance.period.participants.reasons.${v}`, { defaultValue: v }) : ''),
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false,
        customBodyRender: (_v, meta) => {
          const row = data[meta.rowIndex];
          const actions = [];
          if (canEdit && row?.status === 'active') {
            actions.push({ type: 'remove', label: t('performance.period.participants.exclude'), onClick: () => setDrawer({ mode: 'exclude', row }) });
          }
          if (canEdit && row?.status === 'excluded') {
            actions.push({ type: 'return_to_draft', label: t('performance.period.participants.reinclude'), onClick: () => reinclude(row.id) });
          }
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, data, canEdit, reinclude]);

  const handleTableChanged = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, columns));
  }, [columns]);

  const schema = drawer.mode === 'add'
    ? [
      { name: 'employeeId', label: t('performance.period.participants.employee'), type: 'asyncSelect', grid: 12, required: true, ...employeeAsync },
      { name: 'note', label: t('performance.period.participants.addNote'), type: 'textarea', grid: 12, rows: 3, required: true, maxLength: 500 },
    ]
    : [
      {
        name: 'reasonCode', label: t('performance.period.participants.reason'), type: 'select', grid: 12, required: true,
        options: EXCLUSION_REASONS.map((id) => ({ id, name: t(`performance.period.participants.reasons.${id}`) })), idKey: 'id', labelKey: 'name',
      },
      {
        name: 'note', label: t('performance.period.participants.note'), type: 'textarea', grid: 12, rows: 3, maxLength: 500,
        required: (formData) => formData.reasonCode === 'other',
      },
    ];

  const handleSave = useCallback(async (values) => {
    if (drawer.mode === 'add') return add(values.employeeId, values.note);
    return exclude(drawer.row.id, values.reasonCode, values.note);
  }, [drawer, add, exclude]);

  return (
    <>
      {noticeKey && <PeriodNotice>{t(noticeKey)}</PeriodNotice>}
      <DataTable
        title={t('performance.period.participants.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canAdd}
        newButtonLabel={t('performance.period.participants.add')}
        onNewButtonClick={() => setDrawer({ mode: 'add', row: null })}
        filterList={filterList}
        onTableChanged={handleTableChanged}
      />
      <GenericFormDrawer
        open={drawer.mode != null}
        onClose={() => setDrawer({ mode: null, row: null })}
        title={drawer.mode === 'add' ? t('performance.period.participants.addTitle') : t('performance.period.participants.excludeTitle')}
        schema={schema}
        initialValues={{}}
        onSave={handleSave}
      />
    </>
  );
}
